import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Inbox, RefreshCw, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import {
  getWhatsAppInbox, getWhatsAppThread, markWhatsAppThreadRead,
  replyWhatsAppThread, getWhatsAppWebhookStatus, sendWhatsAppFollowUp,
} from '../lib/api';
import { ConversationList } from '../components/whatsapp-inbox/ConversationList';
import { ThreadView } from '../components/whatsapp-inbox/ThreadView';
import { WebhookStatusBar } from '../components/whatsapp-inbox/WebhookStatusBar';

const POLL_MS = 15000;

export default function AdminWhatsAppInbox() {
  const [conversations, setConversations] = useState([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [thread, setThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);
  const selectedRef = useRef(null);

  const loadInbox = useCallback(async () => {
    try {
      const { data } = await getWhatsAppInbox(search);
      setConversations(data.conversations);
      setTotalUnread(data.total_unread);
      window.dispatchEvent(new Event('whatsapp-inbox-updated'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load inbox');
    } finally {
      setLoading(false);
    }
  }, [search]);

  const loadThread = useCallback(async (waId, markRead) => {
    try {
      const { data } = await getWhatsAppThread(waId);
      if (selectedRef.current !== waId) return;
      setThread(data);
      if (markRead && data.messages.some((m) => m.direction === 'in' && !m.read_by_admin)) {
        await markWhatsAppThreadRead(waId);
        loadInbox();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load conversation');
    }
  }, [loadInbox]);

  useEffect(() => {
    const t = setTimeout(loadInbox, 300);
    return () => clearTimeout(t);
  }, [loadInbox]);

  useEffect(() => {
    getWhatsAppWebhookStatus().then((r) => setStatus(r.data)).catch(() => {});
    const id = setInterval(() => {
      loadInbox();
      if (selectedRef.current) loadThread(selectedRef.current, true);
    }, POLL_MS);
    return () => clearInterval(id);
  }, [loadInbox, loadThread]);

  const handleSelect = async (waId) => {
    selectedRef.current = waId;
    setSelected(waId);
    setThread(null);
    setThreadLoading(true);
    await loadThread(waId, true);
    setThreadLoading(false);
  };

  const handleSend = async (text) => {
    setSending(true);
    try {
      await replyWhatsAppThread(selected, text);
      await loadThread(selected, false);
      loadInbox();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reply');
      return false;
    } finally {
      setSending(false);
    }
  };

  const handleFollowUp = async (reference) => {
    try {
      await sendWhatsAppFollowUp(selected, reference);
      toast.success('Follow-up template sent');
      await loadThread(selected, false);
      loadInbox();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send template');
      return false;
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Inbox className="w-6 h-6 text-green-500" />
              WhatsApp Inbox
              {totalUnread > 0 && <Badge className="bg-green-600 text-white" data-testid="inbox-total-unread">{totalUnread} unread</Badge>}
            </h1>
            <p className="text-muted-foreground mt-1 text-sm">Messages customers send to the ParachPay WhatsApp number</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" asChild data-testid="inbox-back-to-settings">
              <Link to="/admin/whatsapp"><ArrowLeft className="w-4 h-4 mr-2" />Notifications</Link>
            </Button>
            <Button variant="outline" size="sm" onClick={loadInbox} data-testid="inbox-refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" />Refresh
            </Button>
          </div>
        </div>
        <WebhookStatusBar status={status} />
        <div className="grid grid-cols-1 md:grid-cols-[340px_1fr] h-[calc(100vh-280px)] min-h-[480px] rounded-2xl border border-border bg-card overflow-hidden">
          <div className={`${selected ? 'hidden md:block' : ''} h-full min-h-0`}>
            <ConversationList
              conversations={conversations}
              loading={loading}
              search={search}
              onSearch={setSearch}
              selected={selected}
              onSelect={handleSelect}
            />
          </div>
          <div className={`${selected ? '' : 'hidden md:block'} h-full min-h-0`}>
            {selected && (
              <button type="button" className="md:hidden text-xs text-muted-foreground px-4 pt-2" onClick={() => { selectedRef.current = null; setSelected(null); setThread(null); }} data-testid="inbox-mobile-back">
                ← All conversations
              </button>
            )}
            <ThreadView thread={thread} loading={threadLoading} onSend={handleSend} sending={sending} onFollowUp={handleFollowUp} />
          </div>
        </div>
      </div>
    </Layout>
  );
}
